import { useEffect, useState, useRef } from 'react';
import {
    FinancingApi,
    LoanStatus2,
    SubmitApplicationDecision,
    LoanApplicationRead,
} from '@servicetitan/financing-api-client';
import {
    PrequalificationStatus,
    PrequalsApi,
    RefreshPrequalResponseDto,
} from '@servicetitan/financing-api-client/src/prequals-api';
import { DecisionDataStore } from '@servicetitan/financing-decision-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useProviderConfig, SetupDataStore } from '@servicetitan/financing-workflow-shared';
import { usePrequalStatusPolling } from '../../prequal-application/hooks/usePrequalStatusPolling';
import { isValidLoanId } from '@servicetitan/financing-utils-shared';

const REFRESH_DELAY_MS = 25000; // 25 seconds
const REFRESH_POLL_INTERVAL_MS = 15000; // 15 seconds
const MAX_POLL_TIME_MS = 120000; // 2 minutes

export interface UseLoanRefreshOptions {
    /** Whether submission was successful (enables refresh) */
    isSubmissionSuccess: boolean;
    /** Prequal ID from submission (for prequal submissions) */
    prequalId?: string | null;
    /** Loan ID from submission (for financing submissions) */
    loanId?: string | null;
    /** Whether this is a hard prequalification */
    isHardPrequalification?: boolean;
    /** Callback when decision data is updated */
    onDecisionUpdate?: (decisionData: any) => void;
}

export interface UseLoanRefreshReturn {
    /** Whether refresh is currently active */
    isRefreshing: boolean;
}

export const useLoanRefresh = (options: UseLoanRefreshOptions): UseLoanRefreshReturn => {
    const {
        isSubmissionSuccess,
        isHardPrequalification,
        prequalId: providedPrequalId,
        loanId: providedLoanId,
        onDecisionUpdate,
    } = options;
    const providerConfig = useProviderConfig();
    const { decisionData, setDecisionData } = DecisionDataStore.useStore();
    const { setupData } = SetupDataStore.useStore();

    const usePrequalSubmission = providerConfig.useFinancingSubmission === false;

    const prequalId = providedPrequalId ?? null;
    const loanId = providedLoanId ?? decisionData?.loanId;

    const [financingApi] = useDependencies(FinancingApi);
    const [prequalsApi] = useDependencies(PrequalsApi);
    const queryClient = useQueryClient();
    const [shouldRefreshLoan, setShouldRefreshLoan] = useState(false);
    const [prequalPlanNumber, setPrequalPlanNumber] = useState<string | null>(null);
    const [prequalApprovedAmount, setPrequalApprovedAmount] = useState<number | null>(null);
    const pollTimeoutRef = useRef<NodeJS.Timeout>();

    const loanRefreshResult = useQuery({
        queryKey: ['loans', loanId, 'refresh'],
        queryFn: () => {
            return financingApi.loansRefreshLoanFromProvider(loanId!);
        },
        enabled:
            (!usePrequalSubmission || isHardPrequalification) &&
            shouldRefreshLoan &&
            isValidLoanId(loanId) &&
            providerConfig.hasAsyncDecision &&
            isSubmissionSuccess,
        refetchInterval: query => {
            // Continue polling if we don't have data yet.
            if (!query.state.data?.data) {
                return REFRESH_POLL_INTERVAL_MS;
            }

            const status = query.state.data.data.status;

            // Keep polling if status is Submitted (not a terminal status).
            if (status === LoanStatus2.Submitted || status === LoanStatus2.AboutToSubmit) {
                return REFRESH_POLL_INTERVAL_MS;
            }

            // Stop polling for terminal statuses (Approved, Declined, Review, etc.).
            return false;
        },
        refetchOnWindowFocus: false,
    });

    const prequalCalculationsResult = useQuery({
        queryKey: ['prequals', prequalId, 'calculations', prequalApprovedAmount, setupData?.state],
        queryFn: () => {
            return (
                prequalsApi
                    .prequalificationsGetPrequalificationCalculations({
                        prequalificationId: prequalId!,
                        amount: prequalApprovedAmount!,
                        state: setupData?.state ?? null,
                    })
                    // Return just the inner "data" property, not the full AxiosResponse.
                    .then(response => response.data)
            );
        },
        enabled:
            usePrequalSubmission &&
            !!prequalPlanNumber &&
            !!prequalApprovedAmount &&
            !!setupData &&
            !!prequalId,
    });

    useEffect(() => {
        if (
            (!usePrequalSubmission || isHardPrequalification) &&
            providerConfig.hasAsyncDecision &&
            loanId &&
            isSubmissionSuccess &&
            !shouldRefreshLoan
        ) {
            const timeoutId = setTimeout(() => {
                setShouldRefreshLoan(true);
            }, REFRESH_DELAY_MS);

            return () => clearTimeout(timeoutId);
        }
    }, [
        usePrequalSubmission,
        isHardPrequalification,
        providerConfig.hasAsyncDecision,
        loanId,
        isSubmissionSuccess,
        shouldRefreshLoan,
    ]);

    // Set up 2-minute timeout for loan refresh polling.
    // This timeout is set once when polling starts and should not reset on every data update.
    useEffect(() => {
        if (
            (!usePrequalSubmission || isHardPrequalification) &&
            shouldRefreshLoan &&
            loanId &&
            providerConfig.hasAsyncDecision
        ) {
            pollTimeoutRef.current = setTimeout(() => {
                // Stop polling after 2 minutes
                queryClient.cancelQueries({ queryKey: ['loans', loanId, 'refresh'] });
                setShouldRefreshLoan(false);

                // Set decision to Error if timeout is reached
                setDecisionData(prevData => ({
                    ...prevData!,
                    decision: SubmitApplicationDecision.Error,
                }));
            }, MAX_POLL_TIME_MS);

            return () => {
                if (pollTimeoutRef.current) {
                    clearTimeout(pollTimeoutRef.current);
                }
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        usePrequalSubmission,
        isHardPrequalification,
        shouldRefreshLoan,
        loanId,
        providerConfig.hasAsyncDecision,
        queryClient,
        // NOTE: decisionData and setDecisionData are intentionally NOT in dependencies
        // to prevent the timeout from resetting on every polling update
    ]);

    useEffect(() => {
        if (loanRefreshResult.data?.data) {
            const loanData = loanRefreshResult.data.data;
            let decision: SubmitApplicationDecision;
            const status = loanData.status;

            switch (status) {
                case LoanStatus2.Approved:
                case LoanStatus2.Prequalified:
                    decision = SubmitApplicationDecision.Approved;
                    break;
                case LoanStatus2.Declined:
                    decision = SubmitApplicationDecision.Declined;
                    break;
                case LoanStatus2.Review:
                    decision = SubmitApplicationDecision.Review;
                    break;
                case LoanStatus2.Submitted:
                case LoanStatus2.AboutToSubmit:
                    decision = SubmitApplicationDecision.Submitted;
                    break;
                default:
                    decision = SubmitApplicationDecision.Error;
                    break;
            }

            // Clear timeout if we reached a terminal status (not Submitted)
            if (
                status !== LoanStatus2.Submitted &&
                status !== LoanStatus2.AboutToSubmit &&
                pollTimeoutRef.current
            ) {
                clearTimeout(pollTimeoutRef.current);
            }

            const updatedDecisionData = {
                ...decisionData!,
                decision,
                loanId: loanData.id ?? null,
                externalLoanId: loanData.externalLoanId ?? null,
                planNumber: loanData.plan?.planNumber ?? null,
                description: loanData.plan?.description ?? loanData.promotionDetails ?? null,
                planType: loanData.plan?.type ?? loanData.creditProduct ?? null,
                approvedAmount: loanData.approvedAmount ?? null,
                term: loanData.plan?.term ?? null,
                rate: loanData.plan?.apr ? parseFloat(loanData.plan.apr) : null,
                payment: loanData.payment ?? null,
                maxCreditAmount: loanData.availableCredit ?? null,
            };

            setDecisionData(updatedDecisionData);

            if (onDecisionUpdate) {
                onDecisionUpdate(updatedDecisionData);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanRefreshResult.data, loanRefreshResult.data?.data?.status, onDecisionUpdate]);

    const pollingEnabled =
        usePrequalSubmission &&
        !isHardPrequalification &&
        isSubmissionSuccess &&
        !!prequalId &&
        providerConfig.hasAsyncDecision === true;

    const { isPolling } = usePrequalStatusPolling({
        prequalId,
        loanId,
        isHardPrequalification,
        enabled: pollingEnabled,
        onStatusChange: (
            status: PrequalificationStatus | LoanStatus2,

            prequalData: RefreshPrequalResponseDto | LoanApplicationRead,
        ) => {
            let decision: SubmitApplicationDecision;

            switch (status) {
                case LoanStatus2.Approved:
                case PrequalificationStatus.Approved:
                case PrequalificationStatus.Prequalified:
                    decision = SubmitApplicationDecision.Approved;
                    break;
                case LoanStatus2.Declined:
                case PrequalificationStatus.Declined:
                    decision = SubmitApplicationDecision.Declined;
                    break;
                case LoanStatus2.Review:
                case PrequalificationStatus.Review:
                    decision = SubmitApplicationDecision.Review;
                    break;
                case LoanStatus2.Error:
                case PrequalificationStatus.Error:
                case LoanStatus2.Cancelled:
                case PrequalificationStatus.Cancelled:
                    decision = SubmitApplicationDecision.Error;
                    break;
                default:
                    // For intermediate statuses (Submitted, AboutToSubmit, etc.), keep Submitted
                    // so the UI stays on the Reviewing component with the stepper
                    decision = SubmitApplicationDecision.Submitted;
                    break;
            }

            // Type guard to check if this is a RefreshPrequalResponseDto
            const isPrequalResponse = 'prequalifiedPlanNumbers' in prequalData;

            const planNumber = isPrequalResponse
                ? prequalData.prequalifiedPlanNumbers &&
                  prequalData.prequalifiedPlanNumbers.length > 0
                    ? prequalData.prequalifiedPlanNumbers[0]
                    : null
                : null;

            const updatedDecisionData = {
                ...decisionData!,
                decision,
                externalLoanId: isPrequalResponse ? (prequalData.externalPrequalId ?? null) : null,
                planNumber,
            };

            setDecisionData(updatedDecisionData);

            if (onDecisionUpdate) {
                onDecisionUpdate(updatedDecisionData);
            }

            // Trigger calculations fetch by setting the plan number and approved amount
            if (planNumber && prequalData.approvedAmount) {
                setPrequalPlanNumber(planNumber);
                setPrequalApprovedAmount(prequalData.approvedAmount);
            }
        },
    });

    useEffect(() => {
        if (prequalCalculationsResult?.data && prequalCalculationsResult?.data.length > 0) {
            const calculation = prequalCalculationsResult.data[0];

            const updatedDecisionData = {
                ...decisionData!,
                approvedAmount: prequalApprovedAmount,
                term: calculation.defaultTerms + calculation.promotionalTerms,
                rate: calculation.defaultRate,
                payment: calculation.defaultMonthlyPayment,
                description: calculation.description ?? null,
            };

            setDecisionData(updatedDecisionData);

            if (onDecisionUpdate) {
                onDecisionUpdate(updatedDecisionData);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prequalCalculationsResult.data, onDecisionUpdate]);

    // For soft prequalifications (not hard), use prequal polling status
    if (usePrequalSubmission && !isHardPrequalification) {
        return {
            isRefreshing: isPolling,
        };
    }

    // For hard prequalifications and regular loan submissions, use loan refresh status
    return {
        isRefreshing: Boolean(
            shouldRefreshLoan && !!loanId && providerConfig.hasAsyncDecision && isSubmissionSuccess,
        ),
    };
};
